import { styled } from "goober";
import { useMemo, useState } from "react";
import {
  defaultManagedAIProvider,
  defaultManagedChatModel,
  MANAGED_AI_PROVIDER_ID,
  resolveAIProviderId,
} from "../../../shared/aiProviders";
import { MANAGED_AI_CONNECTOR_LABEL } from "../../../shared/aiProviders/managedAiConnector";
import { queries, roundedHoursAgo } from "../../../shared/queries";
import { useManagedAIProvider } from "../../hooks/useManagedAIProvider";
import { useQuery } from "../../hooks/useQuery";
import { Dropdown } from "../Dropdown";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import { ProviderLogo } from "../ProviderLogo";
import { Skeleton } from "../Skeleton";
import { Text } from "../Text";
import { AiModelsTablePanel } from "./AiModelsTablePanel";
import { AiOverviewStats } from "./AiOverviewStats";
import { AiSpendByModelPanel } from "./AiSpendByModelPanel";
import { AiSpendVsLimitsPanel } from "./AiSpendVsLimitsPanel";
import {
  computeAiProviderMonitoringRows,
  computeModelCostRows,
  providerLabel,
} from "./aiProviderMonitoring";
import {
  buildModelPricingLookup,
  MANAGED_MODEL_PRICING_ESTIMATES,
} from "../../../shared/aiModelPricing";
import { computeAiUsageSummary } from "./aiUsage";
import { ControlButton } from "./ControlButton";
import { MonitoringLayout } from "./MonitoringLayout";
import { MONITORING_PANEL_HEIGHT } from "./monitoringPanelLayout";
import { AiGrid, Content } from "./workflowsLayout";

const ALL_PROVIDERS = "__all__";

// Empty state fills one large card (like the Value tab); the populated view
// breaks into the smaller panels below.
const EmptyCard = styled("div")`
  flex: 1;
  min-height: 0;
  padding: 32px;
  border-radius: 12px;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProviderTrigger = styled(ControlButton)`
  max-width: 280px;
`;

function LoadingPlaceholder() {
  return (
    <Content aria-hidden>
      <AiGrid>
        <Skeleton style={{ width: "100%", height: 90, borderRadius: 12 }} />
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: 160,
            borderRadius: 12,
          }}
        />
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: 160,
            borderRadius: 12,
          }}
        />
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: MONITORING_PANEL_HEIGHT,
            borderRadius: 12,
          }}
        />
      </AiGrid>
    </Content>
  );
}

export function AiPanel() {
  const since = roundedHoursAgo(30 * 24);
  const [providers, providersQuery] = useQuery(queries.monitorAiProviders());
  const [usageRecords, usageQuery] = useQuery(
    queries.monitoringAiUsageDaily({ since })
  );
  const [pricingRows, pricingQuery] = useQuery(queries.aiModelPricingRows());
  const [budgets, budgetsQuery] = useQuery(queries.monitoringAiBudgets());
  const { managedProvider } = useManagedAIProvider();
  const managed = useMemo(
    () =>
      managedProvider
        ? {
            label: MANAGED_AI_CONNECTOR_LABEL,
            chatModel: defaultManagedChatModel,
            fastModel: defaultManagedAIProvider.fastModel,
          }
        : null,
    [managedProvider]
  );
  const pricing = useMemo(() => {
    const map = buildModelPricingLookup(pricingRows ?? []);
    if (managedProvider) {
      for (const [model, estimate] of Object.entries(
        MANAGED_MODEL_PRICING_ESTIMATES
      )) {
        if (!map.has(model)) map.set(model, estimate);
      }
    }
    return map;
  }, [pricingRows, managedProvider]);

  const rows = useMemo(
    () =>
      computeAiProviderMonitoringRows(
        providers ?? [],
        usageRecords ?? [],
        pricing,
        budgets ?? [],
        managed
      ),
    [providers, usageRecords, pricing, budgets, managed]
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const defaultId = rows.length > 1 ? ALL_PROVIDERS : (rows[0]?.id ?? null);
  const selectionValid =
    selectedId === ALL_PROVIDERS ||
    (selectedId != null && rows.some((r) => r.id === selectedId));
  const effectiveId = selectionValid ? selectedId : defaultId;
  const isAll = effectiveId === ALL_PROVIDERS;

  const providerRecords = useMemo(
    () =>
      isAll
        ? (usageRecords ?? [])
        : (usageRecords ?? []).filter((r) =>
            effectiveId === MANAGED_AI_PROVIDER_ID
              ? r.aiProviderId == null
              : r.aiProviderId === effectiveId
          ),
    [usageRecords, effectiveId, isAll]
  );
  const summary = useMemo(
    () => computeAiUsageSummary(providerRecords, pricing),
    [providerRecords, pricing]
  );
  const modelRows = useMemo(
    () =>
      isAll
        ? computeModelCostRows(providerRecords, pricing)
        : (rows.find((r) => r.id === effectiveId)?.modelRows ?? []),
    [isAll, providerRecords, pricing, rows, effectiveId]
  );
  const spaceBudgets = useMemo(() => {
    if (!isAll) {
      return (budgets ?? []).filter(
        (b) => b.scopeKind === "space" && b.aiProviderId === effectiveId
      );
    }
    // Across all providers a space's cap is the sum of its bespoke per-provider caps.
    const summed = new Map<string, number>();
    for (const b of budgets ?? []) {
      if (b.scopeKind !== "space" || b.limitAmount == null) continue;
      summed.set(b.scopeId, (summed.get(b.scopeId) ?? 0) + b.limitAmount);
    }
    return [...summed.entries()].map(([scopeId, limitAmount]) => ({
      id: scopeId,
      scopeId,
      limitAmount,
    }));
  }, [budgets, effectiveId, isAll]);
  const tenantLimit =
    (budgets ?? []).find((b) => b.scopeKind === "tenant" && !b.aiProviderId)
      ?.limitAmount ?? null;

  if (
    providersQuery.type !== "complete" ||
    usageQuery.type !== "complete" ||
    pricingQuery.type !== "complete" ||
    budgetsQuery.type !== "complete"
  ) {
    return (
      <MonitoringLayout>
        <LoadingPlaceholder />
      </MonitoringLayout>
    );
  }

  if (rows.length === 0) {
    return (
      <MonitoringLayout>
        <Content>
          <EmptyCard>
            <EmptyState
              iconName="brain"
              title="No AI providers"
              description="Add a provider in Settings → AI to start tracking cost and access."
              maxWidth={420}
            />
          </EmptyCard>
        </Content>
      </MonitoringLayout>
    );
  }

  const selectedRow = isAll
    ? null
    : (rows.find((r) => r.id === effectiveId) ?? null);
  const selectedProvider = isAll
    ? null
    : ((providers ?? []).find((p) => p.id === effectiveId) ?? null);

  const providerPicker = (
    <Dropdown
      align="bottom"
      opener={
        <ProviderTrigger type="button">
          {isAll ? (
            <Icon name="layers" size={16} color="var(--text-primary)" />
          ) : effectiveId === MANAGED_AI_PROVIDER_ID && managedProvider ? (
            <Icon name="brain" size={16} color="var(--text-primary)" />
          ) : selectedProvider ? (
            <ProviderLogo
              provider={resolveAIProviderId(selectedProvider)}
              size={16}
            />
          ) : null}
          <Text variant="text-small-md" ellipsis>
            {isAll ? "All providers" : (selectedRow?.label ?? "")}
          </Text>
          <Icon name="chevron-down" size={12} color="var(--text-secondary)" />
        </ProviderTrigger>
      }
      sections={[
        {
          items: [
            ...(rows.length > 1
              ? [
                  {
                    type: "button" as const,
                    key: ALL_PROVIDERS,
                    left: (
                      <Icon
                        name="layers"
                        size={16}
                        color="var(--text-primary)"
                      />
                    ),
                    label: "All providers",
                    selected: isAll,
                    onClick: () => setSelectedId(ALL_PROVIDERS),
                  },
                ]
              : []),
            ...(managedProvider
              ? [
                  {
                    type: "button" as const,
                    key: MANAGED_AI_PROVIDER_ID,
                    left: (
                      <Icon
                        name="brain"
                        size={16}
                        color="var(--text-primary)"
                      />
                    ),
                    label: MANAGED_AI_CONNECTOR_LABEL,
                    selected: effectiveId === MANAGED_AI_PROVIDER_ID,
                    onClick: () => setSelectedId(MANAGED_AI_PROVIDER_ID),
                  },
                ]
              : []),
            ...(providers ?? []).map((provider) => ({
              type: "button" as const,
              key: provider.id,
              left: (
                <ProviderLogo
                  provider={resolveAIProviderId(provider)}
                  size={16}
                />
              ),
              label: providerLabel(provider),
              selected: provider.id === effectiveId,
              right: provider.active ? (
                <Icon name="check-filled" size={12} color="var(--green-600)" />
              ) : undefined,
              onClick: () => setSelectedId(provider.id),
            })),
          ],
        },
      ]}
    />
  );

  return (
    <MonitoringLayout>
      <Content>
        <AiGrid>
          <AiOverviewStats summary={summary} picker={providerPicker} />
          <AiSpendVsLimitsPanel
            providerId={isAll ? null : effectiveId}
            limitAmount={
              isAll ? tenantLimit : (selectedRow?.limitAmount ?? null)
            }
            spendUsd={summary.estimatedCostUsd}
            spaceBudgets={spaceBudgets}
            usageRecords={usageRecords ?? []}
            pricing={pricing}
          />
          <AiSpendByModelPanel modelRows={modelRows} />
          <AiModelsTablePanel records={providerRecords} pricing={pricing} />
        </AiGrid>
      </Content>
    </MonitoringLayout>
  );
}
